import Vue from 'vue';
import { apolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import EventBus from '@/utilities/eventBus';
import logger from 'client-logger';

// Our GraphQL queries/mutations
import GET_SHIPPING_RATES from '@/graphql/queries/getOrderShippingRates.gql';
import GET_USER_ORDERS from '@/graphql/queries/getUserOrders.gql';
import CREATE_CHECKOUT_ORDER from '@/graphql/mutations/createCheckoutOrder.gql';

const apollo = apolloProvider.defaultClient;

// Bring in AppState to be able to show general errors
import AppState from '@/store/AppState';

const Order = new Vue({
	data() {
		return {
			ordersFetched: false,
			allOrders: {},
			allShippingRates: [],
			lastShippingInfo: {},
			lastStoreId: ''
		};
	},
	computed: {
		orders() {
			if (!this.ordersFetched) {
				apollo
					.query({
						query: GET_USER_ORDERS,
						fetchPolicy: 'network-only'
					})
					.then(response => {
						// Update our user data
						const orders = response.data.getUserOrders;

						// Cache in our Vue map
						if (orders.length) {
							// Delete the status key
							if (this.allOrders.status) {
								delete this.allOrders.status;
							}
							for (const order of orders) {
								this.setOrder(order);
							}
						}

						// Set ordersFetched to be true so we don't make this call again
						this.$set(this, 'ordersFetched', true);
					})
					.catch(e => {
						// Set ordersFetched to be true so we don't make this call again
						this.$set(this, 'ordersFetched', true);

						// Update the allOrders object to be null so we know there was an error
						this.$set(this, 'allOrders', null);

						// Returns a nicely formatted GraphQL error object. Cheers!
						const formattedError = AppState.showGeneralError(e);
						logger.error({ error: (formattedError && formattedError.error) || e }, 'Failed to get user orders');
					});
			}

			return this.allOrders;
		},
		/**
		 * This get a list of rates so that the customer can see what options that they would have for shipping
		 * @param {Object} shippingInfo - contains a list of lineItems so we can package them and get shipping
		 * @param {String} storeId -- id for store
		 * @return {Array[LineItem]} -- return a list with the carrier, service, rate, and sometime estimatedShippingDays
		 */
		shippingRates() {
			return (shippingInfo, storeId) => {
				// Check if the info passed in is different than last time, otherwise return the same shipping rates
				if (this.lastShippingInfo !== shippingInfo && this.lastStoreId !== storeId) {
					apollo
						.query({
							query: GET_SHIPPING_RATES,
							variables: {
								shippingInfo,
								storeId
							}
						})
						.then(response => {
							// Update our user data
							const shippingRates = response.data.getOrderShippingRates;

							// Set our data so we don't get a second call
							this.$set(this, 'lastShippingInfo', shippingInfo);
							this.$set(this, 'lastStoreId', storeId);

							this.$set(this, 'allShippingRates', shippingRates);
						})
						.catch(e => {
							// Set our data so we don't get a second call
							this.$set(this, 'lastShippingInfo', shippingInfo);
							this.$set(this, 'lastStoreId', storeId);

							// Update the allShippingRates to null so we know something went wrong
							this.$set(this, 'allShippingRates', null);

							// Returns a nicely formatted GraphQL error object. Cheers!
							const formattedError = AppState.showGeneralError(e);
							logger.error({ error: (formattedError && formattedError.error) || e }, 'Failed to get user orders');
						});
				}

				return this.allShippingRates;
			};
		}
	},
	methods: {
		/**
		 * This is going to place the order in our system
		 * @param {Object} orderJson -- containing the payment, shipment, order, selectedshipping etc
		 * @param {String} storeId -- id for store
		 * @return {Order} - newly created Order
		 */
		async createCheckoutOrder(orderJson, storeId) {
			try {
				const {
					data: { createCheckoutOrder: order }
				} = await apollo.mutate({
					mutation: CREATE_CHECKOUT_ORDER,
					variables: {
						orderJson,
						storeId
						// },
						// update: (cache, { data: { createCheckoutOrder } }) => {
						// 	// Update the cache for this user
						// 	cache.writeQuery({
						// 		query: GET_STORE_BY_ID,
						// 		variables: {
						// 			id: createCheckoutOrder.id
						// 		},
						// 		data: {
						// 			getStoreById: createCheckoutOrder
						// 		}
						// 	});
					}
				});

				// Set the new order
				this.setOrder(order);

				return {
					data: order
				};
			} catch (e) {
				// Check for common errors and show the global modal if present
				// Returns empty object if general errors present; otherwise returns nicely formatted GraphQL error object. Cheers!
				const formattedError = AppState.showGeneralError(e);
				logger.error({ storeId, error: (formattedError && formattedError.error) || e }, 'Failed to create order');

				// if (formattedError && formattedError.error.props.err.code === 'authentication_required') {
				// 	console.info('Will try to confirm');
				// 	const stripe = window.Stripe(process.env.VUE_APP_STRIPE_PK);

				// 	// Charge it anyway!
				// 	try {
				// 		const confirmed = await stripe.confirmCardPayment(formattedError.error.props.err.clientSecret, {
				// 			payment_method: formattedError.error.props.paymentMethodId
				// 		});

				// 		if (confirmed.error) {
				// 			// TODO: if this fails, we need to show the error message
				// 			console.error({ error: confirmed.error }, 'Still did not work!');
				// 		} else {
				// 			// TODO: if this succeeds, we need to finish the transaction with another mutation that finishes creating the order, etc.
				// 			console.info(confirmed, 'cool!');
				// 		}
				// 	} catch (e) {
				// 		console.error({ error: e }, 'Dang it!');
				// 		// TODO: if this fails, we need to show the error message
				// 	}
				// } else {
				return formattedError;
				// }
			}
		},
		/**
		 * Adds the order passed in to allOrders
		 * @param {Object} order - The order being added
		 */
		setOrder(order) {
			this.$set(this.allOrders, order.id, order);
		},
		// A method that will accept incoming subscription payloads and do with them what needs to be done
		subscriptionHandler(payload) {
			// We could have very different actions to execute depending on the scenario
			switch (payload.action) {
				case 'createOrder':
					this.setOrder(payload.order);
					break;
			}
		},
		// Wipes everything out that we have available directly through our props
		clearAll() {
			this.$set(this, 'ordersFetched', false);
			this.$set(this, 'allOrders', {});
		}
	},
	created() {
		// Now to hook into real-time data updates
		EventBus.$on('orders', this.subscriptionHandler);
	}
});

export default Order;
